import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import { animated, useSpring } from 'react-spring';
import { useMutation } from '@apollo/client';

import {
  createLeadMutation,
  VALUATION_MUTATION
} from '../../../../../client/__graphql__/mutations';
import { ILeadInput, ILeadResponse } from '../../../../__types__/global';

import { SiteContext } from '../../../../utils/context/SiteContext';
import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Container } from '../../../layout/Container';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';

import { AccordionGeneral } from '../../../partials/AccordionGeneral';
import { AccordionBenefits } from '../../../partials/AccordionBenefits';
import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { createGTMEvent } from '../../../../utils/tracking/gtm';
import { Hero } from '../../../ui/hero/Index';
import { useProgressiveImage } from '../../../../utils/hooks/useProgressiveImage';
import { Textarea } from '../../../ui/form/Textarea';
import { ConsentHeading } from '../../../ui/form/Consent';
import { Checkbox } from '../../../ui/form/Checkbox';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

interface IRoute {
  office: string;
  path: string;
}

const RaadGiving: React.FC<RouteComponentProps<IRoute>> = ({
  location,
  match
}) => {
  const { routeConfig }: any = React.useContext(SiteContext);
  const { submitWithDigger } = useDigtectiveContext();

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [contact, setContact] = React.useState(true);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const MAIL_MUTATION = createLeadMutation({
    mail: {
      response: `{
            success
          }`
    }
  });

  const [mail, { loading: mailLoading, error }] = useMutation<
    ILeadResponse,
    ILeadInput
  >(MAIL_MUTATION, {
    onCompleted: ({ lead }) => {
      if (lead.mail?.success) {
        setPosted(true);
        setLoading(false);
      }
    }
  });

  const [lead, { error: leadError }] = useMutation<ILeadResponse, ILeadInput>(
    VALUATION_MUTATION,
    {
      onCompleted: ({ lead }) => {
        setLoading(false);
        if (lead.valuation?.success) {
          window.scroll(0, 0);
          setPosted(true);

          // GTM tracking

          createGTMEvent({
            event: 'pmFormSubmission',
            gaEvent: 'Rådgivning NY',
            gaCategory: 'Form',
            gaAction: 'Submit'
          });
        }
      }
    }
  );

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'textarea',
        name: 'comment',
        placeholder: 'Eks: Skal jeg selge eller kjøpe først?',
        value: '',
        /*       label: 'Spørsmål', */
        required: true
      },
      {
        type: 'text',
        name: 'address',
        placeholder: 'Adresse',
        value: ''
        /*  label: 'Adresse' */
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Postnummer *',
        value: '',
        /*  label: 'Postnummer *', */
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Telefon *',
        value: '',
        /*  label: 'Telefon *', */
        /*   labelAfter: '(Brukes til å kontakte deg)', */
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'E-post *',
        value: '',
        /*  label: 'E-post *', */
        /*     labelAfter: '(Brukes til å kontakte deg)', */
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);

      setLoading(true);

      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        if (contact) {
          mail({
            variables: {
              input: {
                ...preparedFields,
                diggerId,
                referer: `${location.pathname}${location.search}`,
                leadType: 'EPOST',
                mailType: 54
              }
            }
          });
        } else {
          mail({
            variables: {
              input: {
                ...preparedFields,
                diggerId,
                referer: `${location.pathname}${location.search}`,
                leadType: 'EPOST',
                mailType: 57
              }
            }
          });
        }
      });
    }
  });

  return (
    <>
      <Seo
        title="Skal jeg selge eller kjøpe først? - PrivatMegleren"
        description="Bør du selge eller kjøpe først? Få hjelp fra PrivatMegleren sine eiendomsmeglere i ditt nærområde og sikre deg de riktige valgene. "
        image="https://cdn.reeltime.no/pm_assets/img/bakgrunner/pmfront.jpg"
        imageTitle="/selge-eller-kjope-bolig-først?"
        url={location.pathname}
      />

      <HeroWrapper>
        <Hero
          imageUrl="https://cdn.reeltime.no/pm_assets/kampanjer/raadgivning/Toppbanner-v3.gif"
          opacity={0.0}
          className="content-height background"
          size="contain"
          alt="bilde"
        ></Hero>
      </HeroWrapper>

      <Container style={pageFadeIn}>
        <FormWrapper style={fadeOut}>
          <SectionWrapper>
            <Section
              className="form-section wider"
              style={{ paddingTop: '30px', paddingBottom: '40px' }}
            >
              <Heading style={{ marginTop: '0px' }} tag="h2" center={true}>
                Selge eller kjope bolig først?
              </Heading>

              <Paragraph center={true} className="form-text">
                Få råd av megleren som kjenner markedet i ditt nærområde best.
                Det er smart å høre med oss før du konkluderer med hva du ønsker
                å gjøre, hvordan og når. Slik sikrer vi at de riktige valgene
                tas for akkurat deg og din eiendom.
              </Paragraph>

              <Paragraph center={true} className="form-text">
                Send oss spørsmålet ditt via skjemaet under så hører du fra oss.
              </Paragraph>
            </Section>
          </SectionWrapper>
          <Form
            className="questionForm"
            onSubmit={handleSubmit}
            noValidate
            /*             style={{ paddingBottom: '50px' }} */
          >
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}

            {fields
              .filter((item) => item?.type === 'textarea')
              .map((item, index) => {
                return (
                  <FormRow
                    className="span questionTextArea"
                    key={`field_${index}`}
                  >
                    <Textarea
                      rows={2}
                      type={item.type}
                      name={item.name}
                      label={item.label}
                      labelAfter={item.labelAfter}
                      placeholder={item.placeholder}
                      value={item.value}
                      onChange={handleChange}
                      error={item.error}
                    />
                  </FormRow>
                );
              })}
            {fields
              .filter((item) => item?.type !== 'textarea')
              .map((item, index) => {
                return (
                  <FormRow key={`field_${index}`}>
                    <Input
                      type={item.type}
                      name={item.name}
                      label={item.label}
                      labelAfter={item.labelAfter}
                      placeholder={item.placeholder}
                      value={item.value}
                      onChange={handleChange}
                      error={item.error}
                    />
                  </FormRow>
                );
              })}

            <Wrapper>
              <CheckboxGroupColumn>
                <ConsentHeading>
                  Jeg anmoder PrivatMegleren om å kontakte meg på
                </ConsentHeading>

                <Checkbox
                  checked={contact}
                  labelPos="right"
                  label="Telefon"
                  onClick={() => setContact((prev) => !prev)}
                />
                <Checkbox
                  checked={!contact}
                  className="right-checkbox"
                  labelPos="right"
                  label="E-post"
                  onClick={() => setContact((prev) => !prev)}
                />
                <ButtonGroup
                  className="raagiving-gruppe"
                  /*                 style={{ justifyContent: 'center' }} */
                >
                  <Button
                    className="raadgiving-button"
                    type="primary"
                    colorTheme="gold"
                    disabled={loading}
                    loading={loading}
                  >
                    Send inn spørsmål
                  </Button>
                  <ButtonLink
                    as="a"
                    href={routeConfig.personvern.url}
                    className="privacy-link-mobile"
                  >
                    Personvernpolicy
                  </ButtonLink>
                </ButtonGroup>
              </CheckboxGroupColumn>
            </Wrapper>
          </Form>
        </FormWrapper>

        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Selge eller kjope bolig først?
            </Heading>
            <Paragraph center={true}>
              En eiendomsmegler vil ta kontakt med deg så snart som mulig.
            </Paragraph>
            <AccordionGeneral />
          </Section>
          <Section className="form-section" style={{ paddingTop: 0 }}>
            <AccordionBenefits />
          </Section>
        </Thanks>
      </Container>
    </>
  );
};

export default RaadGiving;

const Wrapper = styled.div`
  grid-column: span 2;
`;

const FormWrapper = styled(animated.div)`
  .questionTextArea {
    grid-column: span 2;
  }
  textarea {
  }
  .questionForm {
    gap: 1em;
    .raagiving-gruppe {
      grid-column: span 2;
    }

    @media all and (min-width: 600px) {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }
  }
`;

const SectionWrapper = styled.div`
  @media all and (min-width: 600px) {
    .wider {
      max-width: 570px;
    }
  }
`;

const Top: React.FC<{ url: string }> = ({ url }) => {
  const { loaded } = useProgressiveImage({
    url
  });
  const animatedProps = useSpring({
    from: { opacity: 0 },
    to: { opacity: loaded ? 1 : 0 }
  });

  return (
    <StyledTop style={animatedProps}>
      <img src={url} alt="bilde" />
    </StyledTop>
  );
};
const CheckboxGroupColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 550px;
  margin: 0 auto;
  .right-checkbox {
    margin: 0.5rem 0 1rem 0;
  }
`;

const StyledTop = styled(animated.div)`
  margin-top: 66px;
  width: 100%;
  overflow: hidden;

  img {
    width: 100%;
    max-height: 500px;
    object-fit: contain;
    object-position: center;
  }

  @media all and (max-width: 1280px) {
    img {
      min-height: 300px;
      object-fit: cover;
    }
  }
`;

const HeroWrapper = styled.div`
  height: auto;
  padding-top: 7em;
  .background {
    min-height: 200px;
    background-color: #171719;
    max-width: 800px;
    margin: 0 auto;
  }
  @media all and (min-width: 600px) {
    .background {
      min-height: 200px;
      background-color: #171719;
      max-width: 800px;
      margin: 0 auto;
    }
  }
`;
